import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { AppVersionLabel } from '@/components/app-version-label';
import { InstructionsDialog } from '@/components/instructions/InstructionsDialog';
import { ChampionCallout } from '@/components/leaderboard/champion-callout';
import { CarryingLine } from '@/components/lobby/carrying-line';
import { RoomList } from '@/components/lobby/room-list';
import { Button } from '@/components/ui/button';
import { useSession } from '@/hooks/use-session';
import { getDeviceId } from '@/lib/device-id';
import { onIdentityChanged } from '@/lib/session-change';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

/**
 * The landing page — the screen the whole product is judged on.
 *
 * An .io game lives or dies on the distance between arriving and playing, so "Play as guest" is
 * one click: no email, no password, AND NO NAME FIELD. A guest is minted a `Chomp-xxxxxxxx` and
 * cannot choose otherwise — `display_name` is one case-insensitively unique namespace shared
 * with registered accounts, and a guest row costs nothing to make, so a guest who could type a
 * name would be squatting one for free. Picking a name is what registering buys, which is what
 * the line under the button says. Signing in is the secondary path, for players who want their
 * stats — and their name — to follow them.
 *
 * That one click is **Quick play** and it is unchanged: no room id goes with it, and the
 * matchmaker chooses. Under it is the **room browser** — the deployment's declared rooms with
 * live counts — for the player who would rather pick. Both paths end in the same place: `/play`,
 * one `match.join`, one signed ticket. The room id is the only difference between them, and it
 * is a request the server is free to overrule.
 */
function LandingPage() {
  const navigate = useNavigate();
  const { user, isLoading } = useSession();
  const queryClient = useQueryClient();
  const [howToPlayOpen, setHowToPlayOpen] = useState(false);
  /**
   * The room the player picked, held across the guest mutation.
   *
   * A ref rather than state: nothing renders from it, and it has to be readable by the
   * mutation's `onSuccess` callback without that callback closing over a stale render.
   */
  const pendingRoom = useRef<string | undefined>(undefined);
  /** Whether that room was picked to watch rather than to play in. A ref for `pendingRoom`'s reason. */
  const pendingMode = useRef<'spectate' | undefined>(undefined);

  /**
   * Where "Play" and "Watch" both end up: `/play`, with the room the reader picked and — for a
   * spectator — the mode. One navigation for both, because the two differ by a search param and
   * by which tRPC procedure the route then calls.
   */
  const enterArena = () => {
    navigate({
      to: '/play',
      search: { room: pendingRoom.current, mode: pendingMode.current },
    });
  };

  const guest = trpc.auth.guest.useMutation({
    onSuccess: async () => {
      // A guest account is a new identity: whatever this browser cached under the last one
      // is an answer about somebody else.
      await onIdentityChanged(queryClient);
      enterArena();
    },
  });

  const handlePlay = (roomId?: string, mode?: 'spectate') => {
    pendingRoom.current = roomId;
    pendingMode.current = mode;

    // Already signed in (or already a guest from an earlier visit): the session is the entry
    // ticket, so skip straight past the mutation rather than minting a second guest account.
    if (user) {
      enterArena();
      return;
    }

    guest.mutate({ deviceId: getDeviceId() });
  };

  const isBusy = guest.isPending || isLoading;

  return (
    <div className="arena-grid flex flex-1 flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md text-center">
        <h1 className="text-5xl font-black tracking-tight sm:text-6xl">
          Chomp<span className="text-primary">dome</span>
        </h1>
        <p className="mt-3 text-balance text-muted-foreground">
          Eat pellets. Grow. Swallow anyone smaller than you — and duel the ones your own size.
        </p>

        {/* Who is holding a title right now — somebody else, which is the point. It draws
            nothing at all until there is a title to name, so a quiet deployment's landing page
            is exactly the page it was. */}
        <div className="mt-4 flex justify-center">
          <ChampionCallout />
        </div>

        <form
          className="mt-8 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            handlePlay();
          }}
        >
          <Button type="submit" size="hero" disabled={isBusy} className="w-full">
            {guest.isPending ? 'Entering…' : user ? 'Enter the arena' : 'Play as guest'}
          </Button>
          <p className="text-xs text-muted-foreground">
            Quick play — we drop you into the best room going.
            {!user && ' Guests play under a name we pick; create an account to choose your own.'}
          </p>
          {/* What they won somewhere else and will spawn holding — under the button rather than
              beside it, because it is a fact about the next match rather than about the press.
              It draws nothing at all for a guest, for a signed-out reader and for the empty
              wallet nearly everybody has; see `carrying-line.tsx`. */}
          {user && <CarryingLine isGuest={user.isGuest} />}
        </form>

        {guest.error && (
          <p className="mt-3 text-sm text-destructive" role="alert">
            {guest.error.message}
          </p>
        )}

        {/*
         * Watching goes through the same gate playing does — including minting a guest account
         * for a signed-out reader, because `match.spectate` is a protected procedure and a
         * spectator's ticket is a signed ticket like any other.
         */}
        <RoomList
          onPlay={handlePlay}
          onWatch={(roomId) => handlePlay(roomId, 'spectate')}
          busy={isBusy}
        />

        {!user && (
          <p className="mt-4 text-sm text-muted-foreground">
            Want your stats to stick?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Sign in
            </Link>{' '}
            or{' '}
            <Link to="/register" className="font-medium text-primary hover:underline">
              create an account
            </Link>
            .
          </p>
        )}

        <div className="mt-8 flex items-center justify-center gap-4 text-sm">
          <Button variant="link" size="sm" onClick={() => setHowToPlayOpen(true)}>
            How to play
          </Button>
          <Link to="/leaderboard" className="font-medium text-muted-foreground hover:underline">
            Leaderboard
          </Link>
        </div>

        {/* Reachable from the product, not only from the OAuth consent screen: Google expects a
            privacy policy a person can actually find, and so does anybody deciding whether to
            hand over their Google account. */}
        <div className="mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground/70">
          <Link to="/privacy" className="hover:underline">
            Privacy
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms" className="hover:underline">
            Terms
          </Link>
        </div>

        <AppVersionLabel className="mt-2 text-center text-xs text-muted-foreground/70" />
      </div>

      {/*
       * The same button it has always had, opening the modal that grew out of the flat screen
       * behind it: gameplay, power-ups, and one page per registered mini-game. See
       * `components/instructions/InstructionsDialog.tsx` — including why a `Dialog` is right on
       * this surface and on the death panel and nowhere else.
       */}
      <InstructionsDialog open={howToPlayOpen} onClose={() => setHowToPlayOpen(false)} />
    </div>
  );
}
